import { createRouter, createWebHistory } from "vue-router";
import Login from "@/views/LoginForm.vue";
import Dashboard from "@/views/Dashboard.vue";
import PartnerCompany from "@/views/PartnerCompany.vue";
import SuppliersServices from "@/views/SuppliersServices.vue";
import Permissions from "@/views/Permissions.vue";
import PermissionsEdit from "@/views/PermissionsEdit.vue";
import PurchasingFund from "@/views/PurchasingFund.vue";
import PurchasingFundForm from "@/views/PurchasingFundForm.vue";
import Payments from "@/views/Payments.vue";
import PaymentAddEdit from "@/views/PaymentAddEdit.vue";
import Banks from "@/views/Banks.vue";
import Procurement from "@/views/Procurement.vue";
import ProcurementForm from "@/views/ProcurementForm.vue";
import Companies from "@/views/Companies.vue";
import Checks from "@/views/Checks.vue";
import CheckUpsertView from "@/views/CheckUpsertView.vue";
import CheckPrint from "@/views/CheckPrint.vue";
import RegistrationDeeds from "@/views/RegistrationDeeds.vue";
import RegistrationDeedForm from "@/views/RegistrationDeedForm.vue";
import TaxReport from "@/views/reports/TaxReport.vue";
import AccountTreeReport from "@/views/reports/AccountTreeReport.vue";
import ProcurementsReport from "@/views/reports/ProcurementsReport.vue";
import TrialBalanceReport from "@/views/reports/TrialBalanceReport.vue";
import PaymentsReport from "@/views/reports/PaymentsReport.vue";
import CheckReport from "@/views/reports/CheckReport.vue";
import CustomerAccountStatements from "@/views/reports/CustomerAccountStatements.vue";
import PurchasingFundTabs from "@/views/reports/PurchasingFundTabs.vue";
import IncomeStatementReport from "@/views/reports/IncomeStatementReport.vue";
import AccountingTree from "@/views/AccountingTree.vue";
import IncomeStatementView from "@/views/IncomeStatementView.vue";
import SuppliersReport from "@/views/reports/SuppliersReport.vue";
import EditLoginImage from "@/views/EditLoginImage.vue";
import {
  loaded as permLoaded,
  initPermissions,
  has,
  hasAny,
  hasAnyPermission,
  clearPermissions,
} from "@/utils/permissions";

const routes = [
  {
    path: "/",
    name: "Home",
    redirect: () => {
      const token = localStorage.getItem("token");
      if (token) return "/dashboard";
      // Clear any stale perm cache when not logged in
      localStorage.removeItem("perm_ready");
      localStorage.removeItem("perm_granted");
      localStorage.removeItem("perm_user_id");
      const slug = localStorage.getItem("company_id");
      return slug ? `/login/${slug}` : "/not-found";
    },
  },
  {
    path: "/login",
    redirect: () => {
      const slug = localStorage.getItem("company_id");
      return slug ? `/login/${slug}` : "/not-found";
    },
  },
  {
    path: "/login/:slug",
    name: "Login",
    component: Login,
    beforeEnter: (to, from, next) => {
      const token = localStorage.getItem("token");
      token ? next("/dashboard") : next();
    },
  },
  {
    path: "/not-found",
    name: "NotFound",
    component: () => import("@/views/NotFound.vue"),
  },
  {
    path: "/no-access",
    name: "NoAccessBlank",
    component: () => import("@/views/NoAccessBlank.vue"),
    meta: { requiresAuth: true, allowNoPerm: true },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/suppliers/company",
    name: "PartnerCompany",
    component: PartnerCompany,
    meta: { requiresAuth: true },
  },
  {
    path: "/suppliers/company/services",
    name: "SuppliersServices",
    component: SuppliersServices,
    meta: { requiresAuth: true },
  },
  {
    path: "/permissions",
    name: "Permissions",
    component: Permissions,
    meta: {
      requiresAuth: true,
      permAny: ["others.permissions", "permissions.view", "permissions.access"],
    },
  },
  {
    path: "/permissions-edit/:id",
    name: "PermissionsEdit",
    component: PermissionsEdit,
    meta: {
      requiresAuth: true,
      permAny: ["others.permissions", "permissions.edit"],
    },
  },
  {
    path: "/purchasing-fund",
    name: "PurchasingFund",
    component: PurchasingFund,
    meta: { requiresAuth: true },
  },
  {
    path: "/purchasing-fund/form",
    name: "PurchasingFundForm",
    component: PurchasingFundForm,
    meta: { requiresAuth: true },
  },
  {
    path: "/payments",
    name: "Payments",
    component: Payments,
    meta: { requiresAuth: true },
  },
  {
    path: "/payments/add-edit",
    name: "PaymentAddEdit",
    component: PaymentAddEdit,
    meta: { requiresAuth: true },
  },
  {
    path: "/banks",
    name: "Banks",
    component: Banks,
    meta: { requiresAuth: true },
  },
  {
    path: "/procurement",
    name: "Procurement",
    component: Procurement,
    meta: { requiresAuth: true },
  },
  {
    path: "/procurement-edit",
    name: "ProcurementForm",
    component: ProcurementForm,
    meta: { requiresAuth: true },
  },
  {
    path: "/companies",
    name: "Companies",
    component: Companies,
    meta: { requiresAuth: true },
  },
  {
    path: "/checks",
    name: "Checks",
    component: Checks,
    meta: { requiresAuth: true },
  },
  {
    path: "/checks-upsert",
    name: "CheckUpsertView",
    component: CheckUpsertView,
    meta: { requiresAuth: true },
  },
  {
    path: "/checks-print",
    name: "CheckPrint",
    component: CheckPrint,
    meta: { requiresAuth: true },
  },
  {
    path: "/registration-deeds",
    name: "RegistrationDeeds",
    component: RegistrationDeeds,
    meta: { requiresAuth: true },
  },
  {
    path: "/registration-deeds/form",
    name: "RegistrationDeedForm",
    component: RegistrationDeedForm,
    meta: { requiresAuth: true },
    props: (route) => ({
      mode: route.query.mode || "add",
      deedId: route.query.id || null,
    }),
  },
  {
    path: "/reports/tax-report",
    name: "TaxReport",
    component: TaxReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/accountTree-report",
    name: "AccountTreeReport",
    component: AccountTreeReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/procurements",
    name: "ProcurementsReport",
    component: ProcurementsReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/trialBalance",
    name: "TrialBalanceReport",
    component: TrialBalanceReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/payments",
    name: "PaymentsReport",
    component: PaymentsReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/check",
    name: "CheckReport",
    component: CheckReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/suppliers",
    name: "SuppliersReport",
    component: SuppliersReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/customer-account-statements",
    name: "CustomerAccountStatements",
    component: CustomerAccountStatements,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/purchasing-fund-tabs",
    name: "PurchasingFundTabs",
    component: PurchasingFundTabs,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/income-statement",
    name: "income-statement-report",
    component: IncomeStatementReport,
    meta: { requiresAuth: true },
  },
  {
    path: "/reports/income-statement/view",
    name: "income-statement-view",
    component: IncomeStatementView,
    meta: { requiresAuth: true },
  },
  {
    path: "/accounting-tree",
    name: "AccountingTree",
    component: AccountingTree,
    meta: { requiresAuth: true },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/not-found",
  },
{ path: "/login-image", name: "EditLoginImage", component: EditLoginImage, meta: { requiresAuth: true } },];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ========== Navigation Guard ==========
router.beforeEach(async (to, from, next) => {
  const isLoggedIn = !!localStorage.getItem("token");

  if (to.meta?.requiresAuth && !isLoggedIn) {
    clearPermissions();
    const slug = localStorage.getItem("company_id");
    return slug ? next(`/login/${slug}`) : next("/not-found");
  }

  if (!to.meta?.requiresAuth) return next();

  // App.vue loads permissions before router renders anything.
  // This is a safety net for navigations after initial load.
  if (!permLoaded()) {
    try {
      await initPermissions(false);
    } catch (e) {
      clearPermissions();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_name");
      localStorage.removeItem("perm_ready");
      localStorage.removeItem("perm_granted");
      localStorage.removeItem("perm_user_id");
      const slug = localStorage.getItem("company_id");
      return slug ? next(`/login/${slug}`) : next("/not-found");
    }
  }

  if (!to.meta?.allowNoPerm && !hasAnyPermission()) {
    return next({ name: "NoAccessBlank" });
  }

  const need = to.meta?.perm;
  const any  = to.meta?.permAny;
  if (need && !has(need))   return next({ name: "NoAccessBlank" });
  if (any  && !hasAny(any)) return next({ name: "NoAccessBlank" });

  return next();
});

export default router;
